import {
  OdProtosListingsData_Listing_State as ListingState,
  OdProtosListingsData_LwcProduct_State as LwcProductState,
} from '../../__generated__/athenaGlobalTypes';
import { SELECTED_LISTING_LWC_PRODUCT_STATES, SingleLead } from '../../helpers/getAllLeadData';

// extract all strings here into constants, example:
export const ASSIGNMENT_PENDING_STATE_STRING = 'Assignment Pending';
export const NEW_REFERRAL_STATE_STRING = 'New Referral';
export const DIRECT_SALE_STATE_STRING = 'Direct Sale - LA';
export const DNC_COMPETITOR_STATE_STRING = 'DNC - Competitor';
export const DNC_NON_RESPONSIVE_STATE_STRING = 'DNC - Non Responsive';
export const DNC_NOT_BUYING_SELLING_STATE_STRING = 'DNC - Not Selling/Buying';
export const DNC_DIRECT_SALE_STATE_STRING = 'DNC - Direct Sale Only';
export const DNC_NOT_INTERESTED_STATE_STRING = 'DNC - Not Interested';
export const FIRST_CONTACT_ATTEMPTED_STATE_STRING = 'First Contact Attempted';
export const LISTING_PRESENTATION_SCHEDULED_STATE_STRING = 'Listing Presentation Scheduled';
export const LISTING_PRESENTATION_COMPLETE_STATE_STRING = 'Listing Presentation Complete';
export const CREATED_STATE_STRING = 'Created';
export const DELETED_STATE_STRING = 'Deleted';
export const LISTING_AGREEMENT_SIGNED_STATE_STRING = 'Listing Agreement Signed';
export const IN_CONTRACT_STATE_STRING = 'In Contract';
export const LISTED_STATE_STRING = 'Listed';
export const SOLD_ON_MARKET_STATE_STRING = 'Closed';
export const CLOSED_STATE_STRING = 'Closed';
export const UNKNOWN_STATE_STRING = 'Unknown';
export const NOT_ELIGIBLE_STATE_STRING = 'Not Eligible';

export const PostListingStateNames = [
  LISTED_STATE_STRING,
  CLOSED_STATE_STRING,
  IN_CONTRACT_STATE_STRING,
];

const LwcProductStateToUserState = new Map<LwcProductState, string>([
  [LwcProductState.LISTING_INITIATED, NEW_REFERRAL_STATE_STRING],
  [LwcProductState.AGENT_ASSIGNED, NEW_REFERRAL_STATE_STRING],
  [LwcProductState.CONVERTED_DIRECT_SALE, DIRECT_SALE_STATE_STRING], // Pending deprecation
  [LwcProductState.SELECTED_DIRECT_SALE, DIRECT_SALE_STATE_STRING],
  [LwcProductState.DNC_COMPETITOR, DNC_COMPETITOR_STATE_STRING],
  [LwcProductState.DNC_NON_RESPONSIVE, DNC_NON_RESPONSIVE_STATE_STRING],
  [LwcProductState.DNC_NOT_BUYING_SELLING, DNC_NOT_BUYING_SELLING_STATE_STRING],
  [LwcProductState.DNC_DIRECT_SALE_ONLY, DNC_DIRECT_SALE_STATE_STRING],
  [LwcProductState.DNC_NOT_INTERESTED, DNC_NOT_INTERESTED_STATE_STRING],
  [LwcProductState.SOLD_ON_MARKET, SOLD_ON_MARKET_STATE_STRING],
  [LwcProductState.FIRST_CONTACT_ATTEMPTED, FIRST_CONTACT_ATTEMPTED_STATE_STRING],
  [LwcProductState.LISTING_PRESENTATION_SCHEDULED, LISTING_PRESENTATION_SCHEDULED_STATE_STRING],
  [LwcProductState.LISTING_PRESENTATION_COMPLETE, LISTING_PRESENTATION_COMPLETE_STATE_STRING],
  [LwcProductState.CREATED, CREATED_STATE_STRING], // Should not be seen by external agent
  [LwcProductState.DELETED, DELETED_STATE_STRING], //Additional state not in table
  [LwcProductState.NOT_ELIGIBLE, NOT_ELIGIBLE_STATE_STRING],
]);
const ListingStateToUserState = new Map<ListingState, string>([
  [ListingState.LISTING_AGREEMENT_SIGNED, LISTING_AGREEMENT_SIGNED_STATE_STRING],
  [ListingState.IN_CONTRACT, IN_CONTRACT_STATE_STRING],
  [ListingState.LISTING_ACTIVE, LISTED_STATE_STRING],
  [ListingState.SOLD, CLOSED_STATE_STRING],
]);

function getDisplayState(lwcProductState: LwcProductState, listingState: ListingState): string {
  let displayState: string;
  if (SELECTED_LISTING_LWC_PRODUCT_STATES.includes(lwcProductState)) {
    displayState = ListingStateToUserState.get(listingState) ?? UNKNOWN_STATE_STRING;
  } else {
    displayState = LwcProductStateToUserState.get(lwcProductState) ?? UNKNOWN_STATE_STRING;
  }
  return displayState;
}

export function getDisplayStateFromSingleLead(leadDetails: SingleLead): string {
  const lwcProductState = leadDetails.lwcProductData.lwcProductState;
  const listingState = leadDetails.lwcProductData.listingState;
  return getDisplayState(lwcProductState, listingState);
}

type AllowedStateTransitions = {
  FirstContactAttempted: boolean;
  ListingPresentationScheduled: boolean;
  ListingPresentationComplete: boolean;
  ListingAgreementSigned: boolean;
  DidNotConvert: boolean;
  OptInDirectSale: boolean;
  Listed: boolean;
  InContract: boolean;
  Close: boolean;
};

function getAllowedStateTransitions(
  lwcProductState: LwcProductState,
  listingState: ListingState,
  pendingAssignmentAcceptance: boolean,
): AllowedStateTransitions {
  let FirstContactAttempted = false;
  let ListingPresentationScheduled = false;
  let ListingPresentationComplete = false;
  let ListingAgreementSigned = false;
  let Listed = false;
  let InContract = false;
  let Close = false;

  // Can be transitioned to from anywhere
  const DidNotConvert = true;
  const OptInDirectSale = true;

  switch (lwcProductState) {
    // AGENT_ASSIGNED is deprecated, get rid of this case at a good time
    case LwcProductState.AGENT_ASSIGNED:
      FirstContactAttempted = true;
      break;
    case LwcProductState.LISTING_INITIATED:
      FirstContactAttempted = !pendingAssignmentAcceptance;
      break;
    case LwcProductState.FIRST_CONTACT_ATTEMPTED:
      ListingPresentationScheduled = true;
      break;
    case LwcProductState.LISTING_PRESENTATION_SCHEDULED:
      ListingPresentationComplete = true;
      break;
    case LwcProductState.LISTING_PRESENTATION_COMPLETE:
      ListingAgreementSigned = true;
      break;
    default:
      break;
  }

  if (SELECTED_LISTING_LWC_PRODUCT_STATES.includes(lwcProductState)) {
    switch (listingState) {
      case ListingState.LISTING_AGREEMENT_SIGNED:
        Listed = true;
        break;
      case ListingState.LISTING_ACTIVE:
        InContract = true;
        break;
      case ListingState.IN_CONTRACT:
        Close = true;
        break;
      default:
        break;
    }
  }

  return {
    FirstContactAttempted,
    ListingPresentationScheduled,
    ListingPresentationComplete,
    ListingAgreementSigned,
    DidNotConvert,
    OptInDirectSale,
    Listed,
    InContract,
    Close,
  };
}

export function getAllowedStateTransitionsForSingleLead(
  leadDetails: SingleLead,
): AllowedStateTransitions {
  const lwcProductState = leadDetails.lwcProductData.lwcProductState;
  const listingState = leadDetails.lwcProductData.listingState;
  return getAllowedStateTransitions(
    lwcProductState,
    listingState,
    leadDetails.lwcProductData.pendingAssignmentAcceptance,
  );
}

export const isValidDateForLwc = (dateString: string): boolean => {
  const timestamp = Date.parse(dateString);
  // Date.parse() returns NaN for invalid dates
  if (isNaN(timestamp)) {
    return false;
  }

  const parsedDate = new Date(timestamp);
  const year = parsedDate.getFullYear();
  return year >= 2022 && year < 2040; // Will be retired for sure by then
};
